import { queryOptions, useQuery } from '@tanstack/react-query';

import { publicApi } from '@/lib/api-clients/rest-client';
import { getMarketStore } from '@/store/use-markets-store';
import { parseDecimalToBigInt } from '@/utils/value-format';

// TODO: Move to /markets/types or /src/types
type Ticker24hr = {
  symbol: string;
  openPrice: string;
  closePrice: string;
  highPrice: string;
  lowPrice: string;
  quoteVolume: string;
  volume: string;
  priceChange: string | null;
  priceChangePercent: number | null;
  openTime: number;
  closeTime: number;
};

// TODO: Move to /markets/types or /src/types
type Ticker24hrResponse = {
  tickers: Ticker24hr[];
};

const getTicker24hr = (symbols?: string[]): Promise<Ticker24hrResponse> => {
  return publicApi
    .get('/ticker/24hr', {
      params: {
        symbols: symbols?.join(',') || undefined,
      },
    })
    .then((res: Ticker24hrResponse) => {
      res.tickers.forEach((ticker: Ticker24hr) => {
        const { marketData, marketSpec, setMarketData } = getMarketStore(
          ticker.symbol,
        ).getState();

        const priceChange =
          !marketData.priceChange && ticker.priceChange !== null
            ? parseDecimalToBigInt(ticker.priceChange, marketSpec.priceDecimals)
            : null;
        const priceChangePct =
          !marketData.priceChangePct && ticker.priceChangePercent !== null
            ? parseDecimalToBigInt(`${ticker.priceChangePercent}`, 4)
            : null;

        setMarketData({
          volume: parseDecimalToBigInt(ticker.volume, marketSpec.sizeDecimals),
          quoteVolume: parseDecimalToBigInt(
            ticker.quoteVolume,
            marketSpec.priceDecimals + marketSpec.sizeDecimals,
          ),
          ...(priceChange !== null ? { priceChange } : {}),
          ...(priceChangePct !== null ? { priceChangePct } : {}),
        });
      });

      return res;
    });
};

const getTicker24hrQueryOptions = ({
  symbols,
  refetchInterval,
}: { symbols?: string[]; refetchInterval?: number | false } = {}) => {
  return queryOptions({
    queryKey: symbols ? ['ticker24hr', { symbols }] : ['ticker24hr'],
    queryFn: () => getTicker24hr(symbols),
    refetchInterval: refetchInterval ?? 30_000,
  });
};

type UseTicker24hrOptions = {
  symbols?: string[];
  disabled?: boolean;
  refetchInterval?: number | false;
};

export const useTicker24hr = ({
  symbols,
  disabled,
  refetchInterval,
}: UseTicker24hrOptions = {}) => {
  return useQuery({
    ...getTicker24hrQueryOptions({ symbols, refetchInterval }),
    enabled: !disabled,
  });
};
